import React, { useEffect, useRef, useState } from 'react';

import { MailOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { getFormattedDate } from 'helpers/dates';
import { getInitials } from 'helpers/strings';

import { EmailMatchingRecordType } from './types';

type Props = {
  detail: EmailMatchingRecordType;
};

// No allow-scripts: the email's own scripts never run. allow-same-origin is only there so the
// iframe height can be measured from here; links open in a new tab via the injected <base>.
const IFRAME_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox';

const IFRAME_HEAD = '<base target="_blank"><style>body { margin: 0; padding: 20px; overflow: hidden; }</style>';

// Injected after <head> when there is one, so an email's own <!DOCTYPE> stays first and the
// email doesn't drop into quirks mode.
const buildSrcDoc = (emailHtml: string) => {
  const headMatch = emailHtml.match(/<head[^>]*>/i);
  if (!headMatch || headMatch.index === undefined) {
    return `${IFRAME_HEAD}${emailHtml}`;
  }

  const insertAt = headMatch.index + headMatch[0].length;
  return `${emailHtml.slice(0, insertAt)}${IFRAME_HEAD}${emailHtml.slice(insertAt)}`;
};

const Container = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--background);
`;

const Header = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  background: var(--white);
  border-bottom: 1px solid var(--line-gray);
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-bold);
  font-size: 11px;
  text-transform: uppercase;
  color: var(--text-secondary);

  .anticon {
    font-size: 16px;
  }
`;

const HeaderHint = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: scroll;
  padding: 20px;
`;

const EmailCard = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: var(--white);
  border: 1px solid var(--slate-gray-light);
  border-radius: 8px;
  overflow: hidden;
`;

const EmailCardHeader = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--lighter-gray);
`;

const Avatar = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 100px;
  background: var(--deep-blue);
  color: var(--white);
  font-size: 14px;
  font-family: var(--font-bold);
`;

const SenderTexts = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SenderName = styled.span`
  font-size: 14px;
  font-family: var(--font-bold);
`;

const SenderMeta = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
  overflow-wrap: anywhere;
`;

const EmailFrame = styled.iframe`
  display: block;
  width: 100%;
  border: none;
`;

const PlainTextBody = styled.div`
  padding: 24px 28px 28px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const EmailMatchingHtmlPreview = ({ detail }: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [frameHeight, setFrameHeight] = useState(0);
  const emailHtml = detail.email_html || '';

  useEffect(() => {
    setFrameHeight(0);
    return () => resizeObserverRef.current?.disconnect();
  }, [emailHtml]);

  const handleFrameLoad = () => {
    const frameDocument = iframeRef.current?.contentDocument;
    if (!frameDocument?.body) {
      return;
    }

    // Images load after the document does, so keep the height in sync as the content grows.
    const updateHeight = () => setFrameHeight(frameDocument.documentElement.scrollHeight);
    updateHeight();
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = new ResizeObserver(updateHeight);
    resizeObserverRef.current.observe(frameDocument.body);
  };

  const senderName = detail.sender_name || detail.sender_email || '';
  const sentAt = detail.sent_at ? getFormattedDate(detail.sent_at, 'MMM DD, YYYY, hh:mm A') : '';
  const recipientLine = [detail.recipient_email ? `to ${detail.recipient_email}` : '', sentAt]
    .filter(Boolean)
    .join(' · ');

  return (
    <Container>
      <Header>
        <HeaderTitle>
          <MailOutlined />
          HTML Email Preview
        </HeaderTitle>
        <HeaderHint>As received</HeaderHint>
      </Header>

      <ScrollArea className="mini-scroll" data-test-id="email-matching-detail-html-preview">
        <EmailCard>
          <EmailCardHeader>
            <Avatar>{getInitials(senderName) || '?'}</Avatar>
            <SenderTexts>
              <SenderName>{senderName || '-'}</SenderName>
              {detail.sender_name && detail.sender_email && <SenderMeta>&lt;{detail.sender_email}&gt;</SenderMeta>}
              {recipientLine && <SenderMeta>{recipientLine}</SenderMeta>}
            </SenderTexts>
          </EmailCardHeader>

          {emailHtml ? (
            <EmailFrame
              ref={iframeRef}
              title="Email preview"
              sandbox={IFRAME_SANDBOX}
              srcDoc={buildSrcDoc(emailHtml)}
              onLoad={handleFrameLoad}
              style={{ height: frameHeight }}
            />
          ) : (
            <PlainTextBody data-test-id="email-matching-detail-email-body-value">
              {detail.email_body || '-'}
            </PlainTextBody>
          )}
        </EmailCard>
      </ScrollArea>
    </Container>
  );
};

export default EmailMatchingHtmlPreview;
